/**
 * Sentinel used in the filter form to represent "all places selected".
 * The persisted / API representation uses `undefined` instead.
 */
export const ALL_PLACES_ID = 'ALL';

/**
 * Form state uses `['ALL']` for "all places"; persisted/API state uses `undefined`.
 * Strips the sentinel and normalises empty arrays to `undefined` at write boundaries.
 */
export const toPersistedPlaceIds = (
  placeIds: string[] | undefined,
): string[] | undefined => {
  const realIds = (placeIds ?? []).filter((id) => id !== ALL_PLACES_ID);
  return realIds.length > 0 ? realIds : undefined;
};

/** Convert persisted/API placeIds into form state (`['ALL']` = all places). */
export const toFormPlaceIds = (placeIds: string[] | undefined): string[] => {
  return placeIds && placeIds.length > 0 ? placeIds : [ALL_PLACES_ID];
};

/** Migrate persisted filter from legacy `placeId` to `placeIds`. */
export const migratePersistedPlaceIds = (
  cachedFilter:
    | {
        placeIds?: string[] | undefined;
        placeId?: string | undefined;
      }
    | undefined,
  fallback: string[] | undefined,
): string[] | undefined => {
  if (cachedFilter?.placeIds !== undefined) {
    return cachedFilter.placeIds;
  }

  if (cachedFilter?.placeId) {
    return [cachedFilter.placeId];
  }

  return fallback;
};

export const resolvePlaceDisplayNames = (
  placeIds: string[] | undefined,
  places: { id: string; name: string }[] | undefined,
  allPlacesLabel: string,
): string[] => {
  if (!placeIds || placeIds.length === 0) {
    return [allPlacesLabel];
  }

  const names = placeIds.map(
    (placeId) => places?.find((p) => p.id === placeId)?.name ?? placeId,
  );

  if (names.length === 0) {
    return [allPlacesLabel];
  }

  return names;
};
